import React, { useEffect, useState } from 'react';

// Badge counts pending work (not unread messages); opening never dismisses work.
export function useApprovalInbox(open, locationKey) {
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({ items: [], total: 0, has_more: false, loading: true, error: '' });
  useEffect(() => { setPage(0); }, [open, locationKey]);
  useEffect(() => {
    let alive = true;
    let controller;
    let requestVersion = 0;
    let forbidden = false;
    let timer;
    setState(previous => ({ ...previous, items: [], loading: true, error: '' }));
    const load = async () => {
      if (forbidden) return;
      const currentRequest = ++requestVersion;
      controller?.abort();
      controller = new AbortController();
      try {
        const response = await fetch(`/api/approval-notifications?offset=${page * 30}&limit=30`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal,
        });
        // 403 = this user approves nothing, so there is no inbox to show. Stop
        // polling too: every refused poll is a warning in the backend log.
        if (response.status === 403) {
          forbidden = true;
          clearInterval(timer);
          if (alive && currentRequest === requestVersion) setState({ items: [], total: 0, has_more: false, loading: false, error: '' });
          return;
        }
        if (!response.ok) throw new Error('Unable to refresh approvals');
        const data = await response.json();
        if (!data.success || !Array.isArray(data.items)) throw new Error('Unable to refresh approvals');
        if (alive && currentRequest === requestVersion) {
          setState({ ...data, loading: false, error: '' });
          if (page && !data.items.length) setPage(0);
        }
      } catch (e) {
        if (alive && currentRequest === requestVersion && e.name !== 'AbortError') {
          setState({ items: [], total: 0, has_more: false, loading: false, error: 'Approvals could not be refreshed. Please retry.' });
        }
      }
    };
    load();
    timer = setInterval(() => { if (!document.hidden) load(); }, 30000);
    window.addEventListener('focus', load);
    window.addEventListener('aris:approvals-changed', load);
    return () => {
      alive = false;
      controller?.abort();
      clearInterval(timer);
      window.removeEventListener('focus', load);
      window.removeEventListener('aris:approvals-changed', load);
    };
  }, [page, open, locationKey, version]);
  return { ...state, page, setPage, refresh: () => setVersion(v => v + 1) };
}

export default function ApprovalNotifications({ inbox, onOpen }) {
  const { items, total, loading, error, page, setPage, has_more, refresh } = inbox;
  if (!loading && !error && !total) return null;
  return <section aria-label="Pending approvals" className="border-b border-slate-100">
    <div className="px-4 pt-3 pb-2 flex justify-between items-center">
      <h3 className="text-xs font-bold text-teal-800">Pending approvals{total > 0 ? ` (${total})` : ''}</h3>
      <button onClick={refresh} className="text-xs text-teal-700 font-semibold">Refresh</button>
    </div>
    <p className="px-4 pb-2 text-xs text-slate-500">Oldest first. Remains here until handled.</p>
    {loading && <p role="status" className="px-4 py-3 text-sm text-slate-500">Checking approvals…</p>}
    {error && <p role="alert" className="px-4 py-3 text-sm text-amber-800">{error}</p>}
    <div className="divide-y divide-slate-100">
      {items.map(item => <button key={item.key} onClick={() => onOpen(item.url)}
        className="w-full px-4 py-3 text-left hover:bg-teal-50 focus-visible:outline-teal-600">
        <div className="flex justify-between gap-3">
          <span className="text-sm font-semibold text-slate-900 break-words">{item.title}</span>
          {item.amount != null && <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">
            {Number(item.amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })}
          </span>}
        </div>
        <p className="text-xs text-teal-700 mt-1">{item.stage}</p>
        <p className="text-xs text-slate-500 mt-1">{item.center_name || 'Corporate'} · Open review →</p>
      </button>)}
    </div>
    {(page > 0 || has_more) && <div className="flex justify-between items-center px-4 py-3 text-xs">
      <button disabled={!page} onClick={() => setPage(p => p - 1)} className="text-teal-700 disabled:opacity-40">Previous</button>
      <span>Page {page + 1}</span>
      <button disabled={!has_more} onClick={() => setPage(p => p + 1)} className="text-teal-700 disabled:opacity-40">Next</button>
    </div>}
  </section>;
}
